import { useCallback, useEffect, useState, useMemo, type ReactNode } from 'react';
import { type Attachment } from '@clawlet/gateway-client';
import { useAppStore } from '../stores/appStore';
import { useIsMobile } from '../hooks/useIsMobile';
import GearIcon from './icons/GearIcon';
import AppearanceTab from './settings/AppearanceTab';
import EditorTab from './settings/EditorTab';
import GeneralTab from './settings/GeneralTab';
import FamilyTab from './settings/FamilyTab';
import NotificationsTab from './settings/NotificationsTab';
import type { PushNotificationsController } from '../hooks/usePushNotifications';

type SettingsTab = 'appearance' | 'editor' | 'general' | 'notifications' | 'family';

interface SettingsModalProps {
  sendMessage: (text: string, attachments?: Attachment[]) => Promise<void>;
  pushNotifications: PushNotificationsController;
}

interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: ReactNode;
}

const ALL_TABS: TabConfig[] = [
  {
    id: 'appearance',
    label: 'Appearance',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5" />
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
      </svg>
    ),
  },
  {
    id: 'editor',
    label: 'Editor',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
        <path d="m15 5 4 4" />
      </svg>
    ),
  },
  {
    id: 'general',
    label: 'General',
    icon: <GearIcon className="w-5 h-5" />,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
        <path d="M10 17a2 2 0 0 0 4 0" />
      </svg>
    ),
  },
  {
    id: 'family',
    label: 'Family',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
];

export default function SettingsModal({ sendMessage, pushNotifications }: SettingsModalProps) {
  const settingsOpen = useAppStore((state) => state.settingsOpen);
  const closeSettings = useAppStore((state) => state.closeSettings);
  const userRole = useAppStore((state) => state.userRole);
  const [activeTab, setActiveTab] = useState<SettingsTab>('appearance');
  const isMobile = useIsMobile();

  // Filter out 'family' tab for members (non-builders)
  const TABS = useMemo(() => {
    if (userRole === 'member') {
      return ALL_TABS.filter((tab) => tab.id !== 'family');
    }
    return ALL_TABS;
  }, [userRole]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSettings();
      }
    },
    [closeSettings],
  );

  useEffect(() => {
    if (!settingsOpen) {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [settingsOpen, handleKeyDown]);

  if (!settingsOpen) {
    return null;
  }

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col bg-background safe-area-top">
        {/* Mobile header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Settings</h2>
          <button
            type="button"
            onClick={closeSettings}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            title="Close settings"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile tab bar */}
        <div className="flex border-b border-border bg-card">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-accent-foreground border-b-2 border-ring'
                  : 'text-secondary-foreground'
              }`}
            >
              <span className={activeTab === tab.id ? 'text-accent-foreground' : 'text-muted-foreground'}>
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom">
          {activeTab === 'appearance' && <AppearanceTab sendMessage={sendMessage} />}
          {activeTab === 'editor' && <EditorTab />}
          {activeTab === 'general' && <GeneralTab />}
          {activeTab === 'notifications' && <NotificationsTab push={pushNotifications} />}
          {activeTab === 'family' && <FamilyTab />}
        </div>

        <div className="px-4 py-2 border-t border-border">
          <span className="text-[10px] text-muted-foreground">Clawlet v0.1.0</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeSettings();
        }
      }}
    >
      <div className="w-[90vw] max-w-4xl h-[80vh] max-h-[700px] rounded-2xl border border-border bg-background shadow-2xl flex overflow-hidden">
        <nav className="w-52 min-w-52 border-r border-border bg-card flex flex-col">
          <div className="px-4 py-4 border-b border-border">
            <h2 style={{ fontFamily: 'var(--app-heading-font)', fontSize: '1.1rem', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--theme-foreground)' }}>Settings</h2>
          </div>

          <div className="flex-1 p-2 space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-accent text-accent-foreground'
                    : 'text-secondary-foreground hover:text-foreground hover:bg-secondary'
                }`}
              style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'all 0.2s ease' }}
              >
                <span className={activeTab === tab.id ? 'text-accent-foreground' : 'text-muted-foreground'}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-3 border-t border-border">
            <span className="text-[10px] text-muted-foreground">Clawlet v0.1.0</span>
          </div>
        </nav>

        <div className="flex-1 flex flex-col min-w-0">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 id="heading-settings-tab" style={{ fontFamily: 'var(--app-heading-font)', fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--theme-foreground)', textTransform: 'capitalize' }}>{activeTab}</h3>
            <button
              type="button"
              onClick={closeSettings}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              title="Close settings"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {activeTab === 'appearance' && <AppearanceTab sendMessage={sendMessage} />}
            {activeTab === 'editor' && <EditorTab />}
            {activeTab === 'general' && <GeneralTab />}
            {activeTab === 'notifications' && <NotificationsTab push={pushNotifications} />}
            {activeTab === 'family' && <FamilyTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
